import { TFunction } from "i18next";
import { isThisArrayNotEmpty } from "../../../services/helpers/commonHelpers";
import { FieldTypeType, Unit } from "../../../services/helpers/types";
import { ReportBindingType } from "../../organisms/Form/Form";

export const mapValuesFromBindsToString = (
  t: TFunction,
  binds: ReportBindingType[],
): string => {
  return binds
    .map(({ id, value, unit, name, position, type, noteOptionValue }) => {
      const displayUnit = generateUnit(t, unit, value);

      let displayString = `${name}: `;

      if (value !== undefined && value !== null) {
        displayString += value;
      }

      if (displayUnit !== undefined && displayUnit !== null) {
        displayString += ` ${displayUnit}`;
      }

      const uniqueOutput = generateUniqueOutputs(t, name, type, value, unit);
      if (uniqueOutput !== undefined && uniqueOutput !== null) {
        displayString = uniqueOutput;
      }

      if (noteOptionValue) {
        displayString += ` ${noteOptionValue}`;
      }
      return displayString;
    })
    .filter((value) => value !== "")
    .join("\n");
};

const generateUniqueOutputs = (
  t: TFunction,
  key: string,
  type: FieldTypeType,
  value?: string,
  unit?: string,
) => {
  switch (type) {
    case FieldTypeType.StaticText: {
      if (value) {
        const newValue = value?.split("|");

        if (isThisArrayNotEmpty(newValue)) {
          const formattedNewValue = newValue.map((value) => value.trim());
          return formattedNewValue.join("\n");
        }
        return "";
      }
      return "";
    }

    case FieldTypeType.Button: {
      if (value !== "") {
        return value;
      }
      return "";
    }

    case FieldTypeType.Spacer: {
      if (value !== null && value !== "") {
        return `\n${value}`;
      }
      return " ";
    }

    case FieldTypeType.Boolean: {
      if (value === "false") {
        return `${key}: ${t("no")}`;
      }
      if (value === "true") {
        return `${key}: ${t("yes")}`;
      }
      break;
    }
    case FieldTypeType.MultipleChoice:
      const splitValue = value?.split("|");
      if (splitValue !== undefined && splitValue.length > 0 && value !== null) {
        const formattedValue = splitValue.map((value) => value.trim());
        return `${key}: ${formattedValue.join(", ")}`;
      }
      break;

    case FieldTypeType.NumberNumber:
      if (
        value !== undefined &&
        unit !== undefined &&
        value !== null &&
        unit !== null
      ) {
        const [unit1Raw, unit2Raw] = unit.split(",");
        if (value === "0") {
          const unit2 = generateUnit(t, stringToUnit(unit2Raw), "0");

          return `${key}: 0 ${unit2}`;
        }
        const [value1, value2] = value.split(";");
        const unit1 = generateUnit(t, stringToUnit(unit1Raw), value1);
        const unit2 = generateUnit(t, stringToUnit(unit2Raw), value2);
        let displayString = `${key}: `;

        const hasValue1 =
          value1 !== "" &&
          value1 !== undefined &&
          value1 !== null &&
          value1 !== "0";
        const hasValue2 =
          value2 !== "" &&
          value2 !== undefined &&
          value2 !== null &&
          value2 !== "0";

        if (value1 === "0" && value2 === "0") {
          return `${key}: 0 ${unit2}`;
        }

        if (hasValue1) {
          displayString += `${value1} ${unit1}`;
        }
        if (hasValue1 && hasValue2) {
          displayString += " a";
        }
        if (hasValue2) {
          displayString += ` ${value2} ${unit2}`;
        }
        return displayString;
      }
      break;

    case FieldTypeType.Cascader:
      if (value !== undefined && value !== null) {
        const valueArray = value.split("|");
        if (isThisArrayNotEmpty(valueArray)) {
          const formattedValue = valueArray.map((value) => value.trim());
          return `${key}: ${formattedValue.join(", ")}`;
        }
      }
      break;

    case FieldTypeType.TextArea:
      return `${value}`;
    case FieldTypeType.Urine:
      if (
        value === t("negative").toLocaleLowerCase() ||
        value === t("notDone").toLocaleLowerCase()
      ) {
        return `${key}: ${value.toLocaleLowerCase()}`;
      }
      console.log(value);
      const splitUrineValue = value?.split(",");
      const finalString = splitUrineValue?.map((value) => {
        const [key, valueString] = value.split(": ");

        switch (key.trim()) {
          case "Leukocyty":
            return `${t("leukocytes").toLocaleLowerCase()}: ${valueString}`;
          case "Bielkoviny":
            return `${t("proteins").toLocaleLowerCase()}: ${valueString}`;
          case "Nitrity":
            return `${t("nitrites").toLocaleLowerCase()}: ${valueString}`;
          case "Krv":
            return `${t("blood").toLocaleLowerCase()}: ${valueString}`;
          default:
            if (typeof valueString === "string") {
              return undefined;
            }
            return t("negative").toLocaleLowerCase();
        }
      });
      return `${key}: ${finalString?.join(", ").toLocaleLowerCase()}`;
    case FieldTypeType.BMICalculator: {
      if (!value) return "";
      const parts = value.split(";");
      const weight = (parts[0] || "").trim();
      const height = (parts[1] || "").trim();
      if (!weight && !height) return "";

      const parse = (s?: string) => {
        if (!s) return NaN;
        const n = parseFloat(s.replace(",", "."));
        return isNaN(n) ? NaN : n;
      };

      const wNum = parse(weight);
      const hNum = parse(height);
      let bmi = "";
      if (!isNaN(wNum) && !isNaN(hNum) && hNum > 0) {
        const hMeters = hNum / 100;
        bmi = (wNum / (hMeters * hMeters)).toFixed(1);
      }

      return `${t("weight")}: ${weight} kg\n ${t("height")}: ${height} cm\n BMI: ${bmi}`;
    }
    default:
      return undefined;
  }
};

export const generateUnit = (t: TFunction, unit?: Unit, value?: string) => {
  if (value !== undefined && value !== null) {
    switch (unit) {
      case Unit.Day: {
        const numValue = parseFloat(value);
        if (numValue === 1) {
          return t("dayUnit");
        } else if (numValue > 4 || numValue === 0) {
          return t("daysUnit");
        } else if (numValue <= 4) {
          return t("daysUnitLess");
        }
        break;
      }
      case Unit.Degrees: {
        const parsedValue = parseFloat(value);
        if (!isNaN(parsedValue)) {
          return "°C";
        }
        break;
      }
      case Unit.Hours: {
        const numValue = parseFloat(value);
        if (numValue === 1) {
          return t("hourUnit");
        } else if (numValue > 4 || numValue === 0) {
          return t("hoursUnit");
        } else if (numValue <= 4) {
          return t("hoursUnitLess");
        }
        break;
      }
      case Unit.Month: {
        const numValue = parseFloat(value);
        if (numValue === 1) {
          return t("monthUnit");
        } else if (numValue > 4 || numValue === 0) {
          return t("monthsUnit");
        } else if (numValue <= 4) {
          return t("monthsUnitLess");
        }
        break;
      }
      case Unit.PerMinute: {
        return "/min";
      }
      case Unit.Percent: {
        return "%";
      }
      case Unit.PerDay: {
        return `/${t("dayUnit")}`;
      }
      case Unit.MgPerLiter: {
        const parsedValue = parseFloat(value);
        if (!isNaN(parsedValue)) {
          return "mg/l";
        }
        break;
      }
      case Unit.Weight: {
        return "kg";
      }
      case Unit.SizeCM: {
        return "cm";
      }
      case Unit.MMolPerLiter: {
        const parsedValue = parseFloat(value);
        if (!isNaN(parsedValue)) {
          return "mmol/l";
        }
        break;
      }
      case Unit.KgPerM2: {
        const parsedValue = parseFloat(value);
        if (!isNaN(parsedValue)) {
          return "kg/m²";
        }
        break;
      }
    }
  }
};

export const stringToUnit = (unitString: string): Unit => {
  return unitString.trim() as Unit;
};
